const HOME_ROOT_SELECTOR = '[data-hallowvia-home]';
const HOME_REVEAL_SELECTOR = '[data-home-reveal]';
const HOME_REVEAL_ACTIVE_CLASS = 'hallowvia-home-reveals';
const HOME_MOTION_SELECTOR = '.hallowvia-home-hero, .hallowvia-home-ribbon, .hallowvia-home-nightmare';
const HOME_MOTION_VISIBLE_CLASS = 'is-home-motion-visible';
const HOME_REVEAL_GROUPS = [
  '.hallowvia-home-archetypes__grid',
  '.hallowvia-home-audiences__grid',
  '.hallowvia-home-products',
  '.hallowvia-home-editorial__grid',
  '.hallowvia-home-benefits',
  '.hallowvia-home-faq__items',
];
const HOME_REVEAL_STAGGER = 90;
const HOME_REVEAL_MAX_DELAY = 360;

const observedHomeRevealElements = new WeakSet();
const observedHomeMotionElements = new WeakSet();
let homeRevealObserver;
let homeMotionObserver;

function collectElements(root, selector) {
  const elements = [];

  if (root instanceof Element && root.matches(selector)) {
    elements.push(root);
  }

  elements.push(...root.querySelectorAll(selector));
  return elements;
}

function collectHomeRevealElements(root) {
  if (root instanceof Element && root.matches(HOME_ROOT_SELECTOR)) {
    return collectElements(root, HOME_REVEAL_SELECTOR);
  }

  return collectElements(root, `${HOME_ROOT_SELECTOR} ${HOME_REVEAL_SELECTOR}`);
}

function isShopifyDesignMode() {
  return Boolean(window.Shopify?.designMode || document.documentElement.classList.contains('shopify-design-mode'));
}

function applyHomeRevealDelays(root) {
  collectElements(root, HOME_REVEAL_GROUPS.join(',')).forEach((group) => {
    Array.from(group.children)
      .filter((element) => element.matches(HOME_REVEAL_SELECTOR))
      .forEach((element, index) => {
        if (element.hasAttribute('data-home-reveal-delay')) return;

        element.style.setProperty(
          '--home-reveal-delay',
          `${Math.min(index * HOME_REVEAL_STAGGER, HOME_REVEAL_MAX_DELAY)}ms`
        );
      });
  });

  collectElements(root, `${HOME_REVEAL_SELECTOR}[data-home-reveal-delay]`).forEach((element) => {
    const delay = Number.parseInt(element.dataset.homeRevealDelay, 10);
    element.style.setProperty('--home-reveal-delay', `${Number.isFinite(delay) ? delay : 0}ms`);
  });
}

function initializeHomeReveal(root = document) {
  const elements = collectHomeRevealElements(root);
  if (!elements.length) return;

  applyHomeRevealDelays(root);

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reducedMotion || isShopifyDesignMode() || !('IntersectionObserver' in window)) {
    document.documentElement.classList.remove(HOME_REVEAL_ACTIVE_CLASS);
    elements.forEach((element) => element.classList.add('is-visible'));
    return;
  }

  if (!homeRevealObserver) {
    homeRevealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          homeRevealObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10%', threshold: 0.08 }
    );
  }

  elements.forEach((element) => {
    if (observedHomeRevealElements.has(element)) return;
    homeRevealObserver.observe(element);
    observedHomeRevealElements.add(element);
  });

  document.documentElement.classList.add(HOME_REVEAL_ACTIVE_CLASS);
}

function initializeHomeFaq(root = document) {
  collectElements(root, '[data-home-faq-item]').forEach((item) => {
    if (item.dataset.homeFaqReady === 'true') return;

    const summary = item.querySelector('summary');
    if (!summary) return;

    const updateState = () => {
      summary.setAttribute('aria-expanded', item.open ? 'true' : 'false');
      if (!item.open) return;

      const faq = item.closest('.hallowvia-home-faq');
      faq?.querySelectorAll('[data-home-faq-item][open]').forEach((otherItem) => {
        if (otherItem !== item) otherItem.open = false;
      });
    };
    updateState();
    item.addEventListener('toggle', updateState);
    item.dataset.homeFaqReady = 'true';
  });
}

function initializeHomeMotion(root = document) {
  const elements = collectElements(root, HOME_MOTION_SELECTOR);
  if (!elements.length) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reducedMotion || isShopifyDesignMode() || !('IntersectionObserver' in window)) {
    elements.forEach((element) => element.classList.add(HOME_MOTION_VISIBLE_CLASS));
    return;
  }

  if (!homeMotionObserver) {
    homeMotionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.classList.toggle(HOME_MOTION_VISIBLE_CLASS, entry.isIntersecting);
        });
      },
      { rootMargin: '200px 0px', threshold: 0.01 }
    );
  }

  elements.forEach((element) => {
    if (observedHomeMotionElements.has(element)) return;
    homeMotionObserver.observe(element);
    observedHomeMotionElements.add(element);
  });
}

function initializeHomeMenu(root = document) {
  collectElements(root, '.hallowvia-home-header__mobile').forEach((menu) => {
    if (menu.dataset.homeMenuReady === 'true') return;
    const summary = menu.querySelector('summary');
    const panel = menu.querySelector('nav');
    if (!summary || !panel) return;
    let lockedScroller;
    let lockedScrollPosition = 0;

    const currentScroller = () =>
      window.matchMedia('(min-width: 990px)').matches ? document.querySelector('.page-wrapper') : window;

    const readScrollPosition = (scroller) => (scroller === window ? window.scrollY : scroller?.scrollTop || 0);

    const restoreScrollPosition = () => {
      if (!menu.open || !lockedScroller) return;
      if (lockedScroller === window) {
        if (window.scrollY !== lockedScrollPosition) window.scrollTo(0, lockedScrollPosition);
      } else if (lockedScroller.scrollTop !== lockedScrollPosition) {
        lockedScroller.scrollTop = lockedScrollPosition;
      }
    };

    const update = () => {
      summary.setAttribute('aria-expanded', String(menu.open));
      if (menu.open) {
        lockedScroller = currentScroller();
        lockedScrollPosition = readScrollPosition(lockedScroller);
        window.requestAnimationFrame(() => panel.querySelector('a')?.focus());
      } else {
        lockedScroller = undefined;
      }
    };

    const preventBackgroundScroll = (event) => {
      if (menu.open && !panel.contains(event.target)) event.preventDefault();
    };

    menu.addEventListener('toggle', update);
    menu.addEventListener('keydown', (event) => {
      if (!menu.open) return;
      if (event.key === 'Escape') {
        menu.open = false;
        summary.focus();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [summary, ...panel.querySelectorAll('a[href], button:not([disabled])')];
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
    document.addEventListener('click', (event) => {
      if (!menu.open || summary.contains(event.target)) return;

      const link = event.target.closest?.('a');
      if (link && panel.contains(link)) {
        menu.open = false;
        return;
      }

      if (panel.contains(event.target) && event.target !== panel) return;
      menu.open = false;
      summary.focus();
    });
    document.addEventListener('wheel', preventBackgroundScroll, { passive: false });
    document.addEventListener('touchmove', preventBackgroundScroll, { passive: false });
    window.addEventListener('scroll', restoreScrollPosition, { passive: true });
    document.querySelector('.page-wrapper')?.addEventListener('scroll', restoreScrollPosition, { passive: true });
    window.matchMedia('(min-width: 1025px)').addEventListener('change', (event) => {
      if (event.matches && menu.open) menu.open = false;
    });
    update();
    menu.dataset.homeMenuReady = 'true';
  });
}

function initializeHallowviaHome(root = document) {
  if (!document.body.classList.contains('template-index')) return;
  initializeHomeReveal(root);
  initializeHomeMotion(root);
  initializeHomeFaq(root);
  initializeHomeMenu(root);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeHallowviaHome());
} else {
  initializeHallowviaHome();
}

document.addEventListener('shopify:section:load', (event) => initializeHallowviaHome(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  if (homeRevealObserver) {
    collectElements(event.target, HOME_REVEAL_SELECTOR).forEach((element) => homeRevealObserver.unobserve(element));
  }
  if (homeMotionObserver) {
    collectElements(event.target, HOME_MOTION_SELECTOR).forEach((element) => homeMotionObserver.unobserve(element));
  }
});
